import { normalizeAmenities } from "@/lib/amenities";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isPhotoArea, type PhotoEntry } from "@/lib/photoAreas";
import { resolveStorageUrl } from "@/lib/imageUrl";
import { useTenant } from "@/hooks/useTenant";

export type { BedroomDetail } from "@/lib/bedrooms";
import type { BedroomDetail } from "@/lib/bedrooms";

/** One rule's verdict inside a listing's quality_report. */
export interface QualityRuleResult {
  pass: boolean;
  dealBreaker: boolean;
  value?: number | boolean | null;
  threshold?: string | number | null;
  issues?: string[];
  sample?: number;
}

/** Materialized quality verdict for a listing (see quality_control migration). */
export interface QualityReport {
  enabled: boolean;
  computedAt?: string;
  rules?: Record<string, QualityRuleResult>;
  /** Keys of the enabled deal-breaker rules this listing fails. */
  failed?: string[];
}

export interface Property {
  id: string;
  title: string;
  slug: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  size: number | null;
  location: string;
  type: string;
  furnished: boolean;
  pool: boolean;
  oceanView: boolean;
  petFriendly: boolean;
  /** The owner's answer to "Smoking allowed"; null = never asked (detail page only). */
  smokingAllowed?: boolean | null;
  internet: boolean;
  parking: boolean;
  ac: boolean;
  security: boolean;
  image: string;
  images: string[];
  /** Source of truth for the property's photos (ordered, with area tag). */
  photos: PhotoEntry[];
  videos: string[];
  description: string | null;
  amenities: string[];
  /** Essentials the owner explicitly said the home does NOT have. */
  amenitiesAbsent: string[];
  included: {
    electricity: boolean;
    water: boolean;
    internet: boolean;
    cleaning: string;
    minRental: string;
    deposit: string;
  };
  services: {
    cleaning: boolean;
    electricity: boolean;
    gardener: boolean;
    internet: boolean;
    poolManager: boolean;
    propertyManager: boolean;
    water: boolean;
  };
  /**
   * Legacy [lng, lat] tuple. Maintained by a DB trigger that keeps it in
   * sync with `latitude` / `longitude`. Prefer those for new code; this
   * stays around until we eventually drop the column server-side.
   */
  coordinates: [number, number];
  /** Preferred: human-readable lat/lng floats. May be null on rows
   *  imported before the Phase 0 migration. */
  latitude: number | null;
  longitude: number | null;
  available: string[];
  unavailableDates: string[];
  availableDate: string | null;
  availableUntil: string | null;
  bedroomDetails: BedroomDetail[];
  featured: boolean;
  /** ISO timestamp the row was created; the default browse order (newest first). */
  createdAt: string | null;
  status: string;
  owner_id: string | null;
  maxGuests: number;
  ownerName?: string;
  ownerAvatar?: string;
  // New fields
  priceMax: number | null;
  priceVariesByStay: boolean;
  minRentalMonths: number;
  walkingDistanceToBeachMin: number | null;
  parkingSpots: number;
  cleaningDaysPerWeek: number | null;
  tenant_id: string | null;
  /** QC gate: false hides the listing from renters/concierge (owner/admin still
   *  see it). Defaults true; stays true while a tenant's QC config is disabled. */
  qualityVisible: boolean;
  /** Why it's hidden / which checks it fails, drives the owner banner. */
  qualityReport: QualityReport | null;
}

export function mapRow(row: any): Property {
  // Read photos from the new jsonb column; fall back to wrapping legacy
  // image_urls so pre-migration rows still render. Coerces invalid area
  // values to null so a typo in the DB can't break the gallery.
  // URLs are resolved on the current storage host, so rows written while the
  // site ran on another project keep rendering (see resolveStorageUrl).
  const rawPhotos: PhotoEntry[] =
    Array.isArray(row.photos) && row.photos.length > 0
      ? (row.photos as Array<{ url?: unknown; area?: unknown; originalPath?: unknown; alt?: unknown }>)
          .filter((p) => typeof p?.url === "string" && p.url)
          .map((p) => ({
            url: resolveStorageUrl(p.url as string),
            area: isPhotoArea(p.area) ? p.area : null,
            ...(typeof p.originalPath === "string" && p.originalPath ? { originalPath: p.originalPath } : {}),
            ...(typeof p.alt === "string" && p.alt.trim() ? { alt: p.alt.trim().slice(0, 200) } : {}),
          }))
      : ((row.image_urls as string[] | null) ?? [])
          .filter((u): u is string => typeof u === "string" && !!u)
          .map((url) => ({ url: resolveStorageUrl(url), area: null }));

  const imageList = rawPhotos.length > 0 ? rawPhotos.map((p) => p.url) : [];
  const fallbackHero = row.image_url ? resolveStorageUrl(row.image_url) : "/placeholder.svg";

  return {
    id: row.id,
    title: row.title,
    slug: row.slug,
    price: row.price,
    bedrooms: row.bedrooms,
    bathrooms: row.bathrooms,
    size: row.size,
    location: row.location,
    type: row.type,
    furnished: row.furnished,
    pool: row.pool,
    oceanView: row.ocean_view,
    petFriendly: row.pet_friendly,
    smokingAllowed: row.smoking_allowed ?? null,
    internet: row.internet,
    parking: row.parking,
    ac: row.ac,
    security: row.security,
    image: imageList[0] ?? fallbackHero,
    images: imageList.length > 0 ? imageList : [fallbackHero],
    photos: rawPhotos,
    videos: (row.video_urls as string[] | null) ?? [],
    description: row.description,
    amenities: normalizeAmenities(row.amenities),
    amenitiesAbsent: normalizeAmenities(row.amenities_absent),
    included: {
      electricity: row.electricity_included,
      water: row.water_included,
      internet: row.internet_included,
      cleaning: row.cleaning || "Not included",
      minRental: row.min_rental || "1 month",
      deposit: row.deposit_months != null
        ? (Number(row.deposit_months) === 0
            ? "No deposit"
            : `${row.deposit_months} ${Number(row.deposit_months) === 1 ? "month" : "months"} of rent`)
        : (row.deposit || "1 month"),
    },
    services: {
      cleaning: row.cleaning_included ?? false,
      electricity: row.electricity_included ?? false,
      gardener: row.gardener_included ?? false,
      internet: row.internet_included ?? false,
      poolManager: row.pool_manager_included ?? false,
      propertyManager: row.property_manager_included ?? false,
      water: row.water_included ?? false,
    },
    coordinates: row.coordinates as [number, number],
    latitude: row.latitude ?? null,
    longitude: row.longitude ?? null,
    available: row.available_months || [],
    unavailableDates: (row.unavailable_dates as string[]) ?? [],
    availableDate: row.available_date ?? null,
    availableUntil: row.available_until ?? null,
    bedroomDetails: (row.bedroom_details as BedroomDetail[]) ?? [],
    featured: row.featured,
    createdAt: typeof row.created_at === "string" ? row.created_at : null,
    status: row.status,
    owner_id: row.owner_id,
    maxGuests: row.max_guests ?? 4,
    priceMax: row.price_max ?? null,
    priceVariesByStay: row.price_varies_by_stay ?? false,
    minRentalMonths: row.min_rental_months ?? 1,
    walkingDistanceToBeachMin: row.walking_distance_to_beach_min ?? null,
    parkingSpots: row.parking_spots ?? 0,
    cleaningDaysPerWeek: row.cleaning_days_per_week ?? null,
    tenant_id: row.tenant_id ?? null,
    qualityVisible: row.quality_visible ?? true,
    qualityReport: (row.quality_report as QualityReport) ?? null,
  } as Property;
}

// Columns the LIST/grid path actually consumes (cards + filters + map +
// match-scoring + concierge sheet). Deliberately EXCLUDES the detail-only heavy
// columns, description, video_urls, bedroom_details, quality_report, which no
// list/card/filter/scoring consumer reads (verified); the detail page loads
// those via useProperty (select *). mapRow tolerates their absence. Selecting an
// explicit set instead of "*" also stops future wide columns from bloating the
// grid payload automatically.
export const PROPERTY_LIST_COLUMNS = [
  "id", "title", "slug", "price", "bedrooms", "bathrooms", "size", "location",
  "type", "furnished", "pool", "ocean_view", "pet_friendly", "internet",
  "parking", "ac", "security", "photos", "image_urls", "image_url", "amenities", "amenities_absent",
  "electricity_included", "water_included", "internet_included", "cleaning",
  "min_rental", "deposit_months", "deposit", "cleaning_included",
  "gardener_included", "pool_manager_included", "property_manager_included",
  "coordinates", "latitude", "longitude", "available_months", "unavailable_dates",
  "available_date", "available_until", "featured", "status", "owner_id", "max_guests", "price_max",
  "price_varies_by_stay", "min_rental_months", "walking_distance_to_beach_min",
  "parking_spots", "cleaning_days_per_week", "tenant_id", "quality_visible", "created_at",
].join(",");
// The list before the amenity registry SQL was applied: identical minus the
// `amenities_absent` column, so the grid keeps working until it is pasted.
export const PROPERTY_LIST_COLUMNS_LEGACY = PROPERTY_LIST_COLUMNS.replace(",amenities_absent", "");

/** PostgREST's answer when a selected column does not exist yet (42703). */
export const isUndefinedColumn = (e: { code?: string | null; message?: string | null } | null | undefined): boolean =>
  !!e && (e.code === "42703" || /column .* does not exist/i.test(e.message ?? ""));

let warnedLegacyColumns = false;

/**
 * Run a listing query with the full column list, falling back to the legacy
 * list when the database does not have `amenities_absent` yet, so shipping
 * the code before its SQL is pasted cannot take a grid down.
 */
export async function withPropertyColumns<R extends { data: unknown; error: { code?: string | null; message?: string | null } | null }>(
  run: (columns: string) => PromiseLike<R>,
): Promise<R> {
  let res = await run(PROPERTY_LIST_COLUMNS);
  if (res.error && isUndefinedColumn(res.error)) {
    if (!warnedLegacyColumns) {
      warnedLegacyColumns = true;
      console.warn("[properties] amenities_absent column missing, paste the amenity registry SQL");
    }
    res = await run(PROPERTY_LIST_COLUMNS_LEGACY);
  }
  return res;
}

/**
 * The site's live listings; `tenantId` lets an admin panel count another
 * city's. `enabled: false` only reads what another page already cached (the
 * command palette uses it, so a legal page does not download every listing).
 */
export function useProperties(opts: { tenantId?: string; enabled?: boolean } = {}) {
  const tenant = useTenant();
  const tenantId = opts.tenantId ?? tenant.id;
  return useQuery({
    enabled: opts.enabled ?? true,
    // tenant.id in the key so React Query refetches when the tenant changes
    // (relevant for the staging `?tenant=` override; in production the host
    // is fixed so this is a no-op).
    queryKey: ["properties", tenantId],
    queryFn: async () => {
      const select = (columns: string) =>
        supabase
          .from("properties")
          .select(columns)
          .eq("status", "available")
          // Quality-control gate: hide listings that fail the tenant's enabled
          // deal-breaker rules. Defaults to true (visible) and stays true while
          // a tenant's quality config is disabled, so this is a no-op until an
          // admin opts in. Owner/admin views deliberately skip this filter.
          .eq("quality_visible", true)
          // Multi-tenant scope via the explicit tenant_id column (set on
          // create + back-filled by migration). Sidesteps area-name collisions
          // across cities (e.g. "Playa Hermosa" exists in both Santa Teresa
          // region and Guanacaste).
          .eq("tenant_id", tenantId)
          .order("price", { ascending: false });
      const { data, error } = await withPropertyColumns(select);
      if (error) throw error;
      const props = (data || []).map(mapRow);
      
      const ownerIds = [...new Set(props.map(p => p.owner_id).filter(Boolean))] as string[];
      if (ownerIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, display_name, avatar_url")
          .in("user_id", ownerIds);
        const profileMap = new Map(profiles?.map(p => [p.user_id, p]) ?? []);
        for (const prop of props) {
          if (prop.owner_id) {
            const profile = profileMap.get(prop.owner_id);
            prop.ownerName = profile?.display_name ?? undefined;
            prop.ownerAvatar = profile?.avatar_url ?? undefined;
          }
        }
      }
      return props;
    },
  });
}

export function useProperty(slug: string | undefined) {
  return useQuery({
    queryKey: ["property", slug],
    queryFn: async () => {
      if (!slug) throw new Error("No slug");
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("slug", slug)
        .single();
      if (error) throw error;
      return mapRow(data);
    },
    enabled: !!slug,
  });
}
